import type {
  MarketplaceMarketRouteDataQuery,
  MarketplaceZoneRouteDataQuery,
} from 'components/marketplace/browse/__generated__/marketplace-browse.zeus';
import {
  normalizeSearchVertices,
  type PropertySearchQuery,
} from 'components/marketplace/helpers/useSearchQueries';
import type { MarketInfo } from 'components/marketplace/map/types';
import { getZoneFrameBounds } from 'components/marketplace/map/utils';

type MarketplaceMarket = NonNullable<MarketplaceMarketRouteDataQuery['marketplace']['market']>;
type MarketplaceZone = NonNullable<MarketplaceZoneRouteDataQuery['marketplace']['zone']>;

// Pure builders shared by server rendering and the browser, which applies a
// saved search on the page. Keep server-only imports out of this module.

const viewportMarketInfo = (
  queryParams: PropertySearchQuery,
  fallback: Omit<MarketInfo, 'shape'>,
): MarketInfo | null => {
  const vertices = normalizeSearchVertices(queryParams.vertices);
  if (!vertices) {
    return null;
  }
  return {
    ...fallback,
    shape: { coordinates: [vertices], type: 'Polygon' },
  };
};

export const buildMarketInfoFromMarket = (
  market: MarketplaceMarket,
  queryParams: PropertySearchQuery,
): MarketInfo | false => {
  const base = {
    displayName: market.displayName,
    identifier: market.identifier,
    name: market.identifier,
  };
  const viewport = viewportMarketInfo(queryParams, base);
  if (viewport) {
    return viewport;
  }

  const polygonCoordinates = market.shape?.rings.map((ring) =>
    ring.points.map(({ latitude: lat, longitude: lon }) => ({ lat, lon })),
  );
  if (!polygonCoordinates?.some((ring) => ring.length > 0)) {
    return false;
  }

  return { ...base, shape: { coordinates: polygonCoordinates, type: 'Polygon' } };
};

export const buildMarketInfoFromZone = (
  zone: MarketplaceZone,
  queryParams: PropertySearchQuery,
): MarketInfo | false => {
  const base = { displayName: zone.name, identifier: null, name: zone.name };
  const urlViewport = viewportMarketInfo(queryParams, base);
  if (urlViewport) {
    return urlViewport;
  }
  const bounds = getZoneFrameBounds(zone);
  if (!bounds) {
    return false;
  }
  const [[west, south], [east, north]] = bounds;

  return {
    ...base,
    shape: {
      coordinates: [
        [
          { lat: south, lon: west },
          { lat: north, lon: west },
          { lat: north, lon: east },
          { lat: south, lon: east },
          { lat: south, lon: west },
        ],
      ],
      type: 'Polygon',
    },
  };
};
